import { useEffect, useState, useCallback } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, Send, Save, CheckCircle2, FileSignature, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import TiptapEditor from "@/components/TiptapEditor";

const TEMPLATE_META = {
  pv_completion: {
    label: "PV de fin de chantier",
    desc: "Envoyé automatiquement dès qu'une commande passe en statut « Terminée » pour proposer un RDV de signature.",
    icon: FileSignature,
    color: "text-blue-600",
    bg: "bg-blue-50",
    showDelayDays: false,
  },
  deposit_reminder: {
    label: "Rappel d'acompte",
    desc: "Envoyé (manuellement ou en auto) lorsque l'acompte n'a pas été réglé après X jours.",
    icon: AlertCircle,
    color: "text-amber-600",
    bg: "bg-amber-50",
    showDelayDays: true,
  },
  balance_reminder: {
    label: "Rappel de solde",
    desc: "Envoyé (manuellement ou en auto) lorsque le solde n'a pas été réglé X jours après la clôture.",
    icon: CheckCircle2,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    showDelayDays: true,
  },
};

const TemplateCard = ({ tpl, variables, onSaved }) => {
  const meta = TEMPLATE_META[tpl.type] || { label: tpl.type, desc: "", icon: Mail, color: "text-slate-600", bg: "bg-slate-50" };
  const Icon = meta.icon;
  const [form, setForm] = useState(tpl);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => { setForm(tpl); setDirty(false); }, [tpl]);

  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        from_name: form.from_name,
        from_email: form.from_email,
        reply_to: form.reply_to,
        subject: form.subject,
        body_html: form.body_html,
      };
      if (tpl.type === "pv_completion") payload.auto_send = form.auto_send;
      if (tpl.type !== "pv_completion") {
        payload.auto_send = form.auto_send;
        if (form.auto_send_delay_days !== undefined && form.auto_send_delay_days !== null && form.auto_send_delay_days !== "") {
          payload.auto_send_delay_days = parseInt(form.auto_send_delay_days);
        }
      }
      const r = await api.updateEmailTemplate(tpl.type, payload);
      toast.success("Template enregistré");
      setForm(r.data);
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) { toast.error("Email invalide"); return; }
    setTesting(true);
    try {
      await api.testSendEmailTemplate(tpl.type, { to: testEmail });
      toast.success(`Email test envoyé à ${testEmail}`);
    } catch (e) {
      toast.error(getErrorMessage(e, "Envoi échoué"));
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card data-testid={`email-template-${tpl.type}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`w-9 h-9 rounded-md ${meta.bg} ${meta.color} inline-flex items-center justify-center shrink-0`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                {meta.label}
                {form.enabled ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Actif</Badge>
                ) : (
                  <Badge variant="outline">Désactivé</Badge>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{meta.desc}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs" htmlFor={`enabled-${tpl.type}`}>Activer</Label>
            <Switch
              id={`enabled-${tpl.type}`}
              checked={!!form.enabled}
              onCheckedChange={(v) => update({ enabled: v })}
              data-testid={`email-${tpl.type}-enabled`}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Auto-send row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-muted/30 rounded-md">
          <div className="flex items-center gap-3">
            <Switch
              id={`auto-${tpl.type}`}
              checked={!!form.auto_send}
              onCheckedChange={(v) => update({ auto_send: v })}
              disabled={!form.enabled}
              data-testid={`email-${tpl.type}-auto`}
            />
            <Label htmlFor={`auto-${tpl.type}`} className="text-sm">
              {tpl.type === "pv_completion"
                ? "Envoi automatique sur passage en Terminée"
                : "Envoi automatique après délai"}
            </Label>
          </div>
          {meta.showDelayDays && (
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap" htmlFor={`delay-${tpl.type}`}>Délai (jours)</Label>
              <Input
                id={`delay-${tpl.type}`}
                type="number"
                min={0}
                max={365}
                className="w-24"
                value={form.auto_send_delay_days ?? ""}
                onChange={(e) => update({ auto_send_delay_days: e.target.value })}
                disabled={!form.auto_send || !form.enabled}
                data-testid={`email-${tpl.type}-delay`}
              />
            </div>
          )}
        </div>

        {/* Sender fields */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Nom expéditeur</Label>
            <Input value={form.from_name || ""} onChange={(e) => update({ from_name: e.target.value })} placeholder="Ex: Bati Assistant" data-testid={`email-${tpl.type}-from-name`} />
          </div>
          <div>
            <Label className="text-xs">Email expéditeur</Label>
            <Input value={form.from_email || ""} onChange={(e) => update({ from_email: e.target.value })} placeholder="Hérité si vide" data-testid={`email-${tpl.type}-from-email`} />
          </div>
          <div>
            <Label className="text-xs">Reply-To (optionnel)</Label>
            <Input value={form.reply_to || ""} onChange={(e) => update({ reply_to: e.target.value })} placeholder="Ex: direction@…" data-testid={`email-${tpl.type}-reply-to`} />
          </div>
        </div>

        {/* Subject */}
        <div>
          <Label className="text-xs">Objet du mail</Label>
          <Input
            value={form.subject || ""}
            onChange={(e) => update({ subject: e.target.value })}
            placeholder="Utilisez {{ reference }} pour insérer des variables"
            data-testid={`email-${tpl.type}-subject`}
          />
        </div>

        {/* Body */}
        <div>
          <Label className="text-xs">Corps du mail (HTML)</Label>
          <TiptapEditor
            value={form.body_html || ""}
            onChange={(html) => update({ body_html: html })}
            variables={variables}
            testid={`email-${tpl.type}-body`}
          />
        </div>

        {/* Test send + Save */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
          <Input
            type="email"
            placeholder="test@exemple.fr"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="max-w-[240px]"
            data-testid={`email-${tpl.type}-test-email`}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={sendTest}
            disabled={testing || !testEmail}
            data-testid={`email-${tpl.type}-test-send`}
          >
            {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Envoyer un test
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {dirty && <span className="text-xs text-amber-700">Modifications non enregistrées</span>}
            <Button
              size="sm"
              onClick={save}
              disabled={saving || !dirty}
              data-testid={`email-${tpl.type}-save`}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Enregistrer
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const EmailsTab = () => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tpls, vars] = await Promise.all([
        api.listEmailTemplates(),
        api.getEmailTemplateVariables(),
      ]);
      setTemplates(tpls.data || []);
      setVariables(vars.data?.variables || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && templates.length === 0) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="emails-tab">
      <div className="bg-muted/30 rounded-lg p-3 text-sm text-muted-foreground">
        Configurez les emails automatiques envoyés à vos clients. Cliquez sur les variables pour les insérer dans l'objet ou le corps. Activez / désactivez chaque template individuellement.
      </div>
      {templates.map((tpl) => (
        <TemplateCard key={tpl.type} tpl={tpl} variables={variables} onSaved={load} />
      ))}
    </div>
  );
};

export default EmailsTab;
